import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { logActivity } from '../../lib/activity';
import { Loader2, Trash2, AlertCircle, Calculator, Scale, Copy, Archive, RefreshCw, Plus } from 'lucide-react';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import { DuplicateQuoteModal } from './components/DuplicateQuoteModal';
import { formatAmount } from '../datasets/utils/formatters';

interface Quotation {
  id: string;
  name: string;
  created_at: string;
  archived: boolean;
  archived_at: string | null;
  bank: {
    name: string;
    alias: string;
  };
  estimated_payments: number;
  estimated_payouts: number;
  average_amount: number;
  payment_approved_fixed_fee: number;
  payment_approved_percentage_fee: number;
  payment_approved_max_cap: number;
  payout_approved_fixed_fee: number;
  payout_approved_percentage_fee: number;
  payout_approved_max_cap: number;
}

export function QuotationList() {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [quotationToDelete, setQuotationToDelete] = useState<Quotation | null>(null);
  const [quotationToDuplicate, setQuotationToDuplicate] = useState<Quotation | null>(null);
  const [selectedQuotes, setSelectedQuotes] = useState<string[]>([]);
  const [duplicating, setDuplicating] = useState(false);
  const [archiving, setArchiving] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    loadQuotations();
  }, []);

  const loadQuotations = async () => {
    try {
      const { data, error } = await supabase
        .from('quotations')
        .select(`
          *,
          bank:banks (
            name,
            alias
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotations(data || []);
    } catch (err) {
      console.error('Error loading quotations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!quotationToDelete) return;
    
    setArchiving(quotationToDelete.id);
    setDeleteError(null);

    try {
      const { error } = await supabase
        .rpc('toggle_quote_archive', {
          quote_id_param: quotationToDelete.id,
          archive: true
        });

      if (error) throw error;
      
      await logActivity(
        'Archived',
        'quotation',
        quotationToDelete.id,
        quotationToDelete.name
      );
      
      await loadQuotations();
      setQuotationToDelete(null);
    } catch (err) {
      console.error('Error deleting quotation:', err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete quotation');
    } finally {
      setArchiving(null);
    }
  };

  const handleRestore = async (quotationId: string) => {
    setArchiving(quotationId);
    
    try {
      const { error } = await supabase
        .rpc('toggle_quote_archive', {
          quote_id_param: quotationId,
          archive: false
        });

      if (error) throw error;
      
      const quotation = quotations.find(q => q.id === quotationId);
      if (quotation) {
        await logActivity('Restored', 'quotation', quotationId, quotation.name);
      }
      
      await loadQuotations();
    } catch (err) {
      console.error('Error restoring quotation:', err);
      setError(err instanceof Error ? err.message : 'Failed to restore quotation');
    } finally {
      setArchiving(null);
    }
  };

  const handleDuplicate = async (newName: string) => {
    if (!quotationToDuplicate) return;
    
    setDuplicating(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .rpc('duplicate_quotation', {
          quotation_id: quotationToDuplicate.id,
          new_name: newName
        });

      if (error) throw error;
      
      loadQuotations();
      setQuotationToDuplicate(null);
    } catch (err) {
      console.error('Error duplicating quotation:', err);
      setError(err instanceof Error ? err.message : 'Failed to duplicate quotation');
    } finally {
      setDuplicating(false);
    }
  };

  const toggleQuoteSelection = (quoteId: string) => {
    setSelectedQuotes(prev => {
      if (prev.includes(quoteId)) {
        return prev.filter(id => id !== quoteId);
      }
      if (prev.length < 2) {
        return [...prev, quoteId];
      }
      return [prev[1], quoteId];
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-[14px] text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (quotations.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Calculator className="w-7 h-7 text-slate-400" />
        </div>
        <h3 className="text-[15px] font-semibold text-slate-900">No quotations</h3>
        <p className="mt-1 text-[14px] text-slate-500">Get started by creating a new quotation.</p>
        <div className="mt-6">
          <Link to="/quotations/new" className="btn-primary">
            <Plus className="w-4 h-4" />
            Create new quotation
          </Link>
        </div>
      </div>
    );
  }

  const filteredQuotations = quotations.filter(q => !q.archived || includeArchived);

  return (
    <div className="space-y-6">
      {deleteError && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-[13px] text-red-600">{deleteError}</p>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Quotations</h1>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <p className="page-subtitle">Manage your fee quotations</p>
            <button
              onClick={() => setIncludeArchived(!includeArchived)}
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-[13px] font-medium rounded-lg transition-colors ${
                includeArchived
                  ? 'text-slate-700 bg-slate-100 hover:bg-slate-200'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Archive className="w-3.5 h-3.5" />
              {includeArchived ? 'Hide Archived' : 'Show Archived'}
            </button>
            {selectedQuotes.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-slate-400">
                  {selectedQuotes.length} selected
                </span>
                {selectedQuotes.length === 2 && (
                  <Link
                    to={`/quotations/compare/${selectedQuotes.join(',')}`}
                    className="btn-secondary !py-1 !text-[13px]"
                  >
                    <Scale className="w-3.5 h-3.5" />
                    Compare
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
        <Link to="/quotations/new" className="btn-primary">
          <Plus className="w-4 h-4" />
          New Quotation
        </Link>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredQuotations.map((quotation) => (
          <div
            key={quotation.id}
            className={`card-hover group ${quotation.archived ? 'opacity-60' : ''} ${selectedQuotes.includes(quotation.id) ? 'ring-2 ring-violet-500' : ''}`}
          >
            <div className="p-5">
              <div className="flex justify-between items-start gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={selectedQuotes.includes(quotation.id)}
                    onChange={() => toggleQuoteSelection(quotation.id)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  <Link to={`/quotations/${quotation.id}`} className="flex-1 min-w-0">
                    <h2 className="text-[15px] font-semibold text-slate-900 group-hover:text-violet-600 transition-colors truncate">
                      {quotation.name}
                    </h2>
                    <p className="mt-0.5 text-[12px] text-slate-400">
                      {quotation.bank ? `${quotation.bank.name} (${quotation.bank.alias})` : 'Custom Fee Structure'}
                    </p>
                  </Link>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {quotation.archived ? (
                    <button
                      onClick={() => handleRestore(quotation.id)}
                      disabled={archiving === quotation.id}
                      className="btn-ghost"
                      title="Restore"
                    >
                      {archiving === quotation.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setQuotationToDuplicate(quotation)}
                        className="btn-ghost"
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setQuotationToDelete(quotation)}
                        disabled={archiving === quotation.id}
                        className="btn-ghost hover:!text-red-500 hover:!bg-red-50"
                        title="Archive"
                      >
                        {archiving === quotation.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {quotation.archived && (
                <div className="mt-2">
                  <span className="badge-gray">
                    <Archive className="w-3 h-3" />
                    Archived {new Date(quotation.archived_at!).toLocaleDateString()}
                  </span>
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="stat-label">Payments</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">{quotation.estimated_payments.toLocaleString()}</p>
                </div>
                <div>
                  <p className="stat-label">Payouts</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">{quotation.estimated_payouts.toLocaleString()}</p>
                </div>
              </div>

              <div className="mt-3">
                <p className="stat-label">Avg Amount</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">{formatAmount(quotation.average_amount)}</p>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="stat-label">Fee Structure</p>
                <div className="mt-2 grid grid-cols-2 gap-4 text-[13px]">
                  <div>
                    <p className="font-medium text-slate-600 mb-1">Payments</p>
                    <div className="space-y-0.5 text-slate-500">
                      <p>Fixed: {formatAmount(quotation.payment_approved_fixed_fee)}</p>
                      <p>Rate: {quotation.payment_approved_percentage_fee}%</p>
                      <p>Cap: {formatAmount(quotation.payment_approved_max_cap)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-slate-600 mb-1">Payouts</p>
                    <div className="space-y-0.5 text-slate-500">
                      <p>Fixed: {formatAmount(quotation.payout_approved_fixed_fee)}</p>
                      <p>Rate: {quotation.payout_approved_percentage_fee}%</p>
                      <p>Cap: {formatAmount(quotation.payout_approved_max_cap)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5 py-3 bg-slate-50 border-t border-slate-100">
              <span className="text-[12px] text-slate-400">
                Created {new Date(quotation.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>

      <DeleteConfirmationModal
        title="Archive Quotation"
        message={`Are you sure you want to archive "${quotationToDelete?.name}"? You can restore it later.`}
        isOpen={!!quotationToDelete}
        isDeleting={!!deleting}
        onConfirm={handleDelete}
        onCancel={() => setQuotationToDelete(null)}
      />
      <DuplicateQuoteModal
        quotation={quotationToDuplicate}
        isOpen={!!quotationToDuplicate}
        isDuplicating={duplicating}
        onConfirm={handleDuplicate}
        onCancel={() => setQuotationToDuplicate(null)}
      />
    </div>
  );
}
